// ────────────────────────────────────────────────────────────────
// src/pages/QuickPayGuestPage.js
//
// Public page (QR).  Guest pays a PayMe user via Stripe.
//
//  • Clean centred layout (no left hero) – fully mobile-friendly
//  • i18n (en / ja / pt)
//  • Modal for success & error
//  • Name, e-mail & amount required (min ¥50)
//  • Builds PaymentRequest only after canMakePayment()
// ────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js'
import axios from 'axios'

function QuickPayGuestPage() {
  const { token } = useParams()
  const navigate  = useNavigate()
  const stripe    = useStripe()
  const elements  = useElements()

  /* ─── language & i18n ─── */
  const lang = localStorage.getItem('lang') || 'en'
  const i18n = {
    en: {
      pageTitle    : 'Send money to',
      amountLabel  : 'Amount (¥ – min 50)',
      nameLabel    : 'Your Name *',
      emailLabel   : 'Your E-mail *',
      methodLabel  : 'Payment Method',
      payBtn       : 'Pay',
      processing   : 'Processing…',
      successTtl   : '🎉 Payment Successful',
      successMsg   : (owner)=>`You just sent money to ${owner}!\n\nOpen a free PayMe account and start receiving payments in seconds.\nhttps://www.payme.co.jp`,
      errorTtl     : 'Error',
      ok           : 'OK',
      invalidAmt   : 'Amount must be at least ¥50',
      missingInfo  : 'Name, e-mail and amount are required',
      done         : 'Done',
    },
    ja: {
      pageTitle    : '次のユーザーへ送金:',
      amountLabel  : '金額 (¥ – 最低50)',
      nameLabel    : 'お名前 *',
      emailLabel   : 'メールアドレス *',
      methodLabel  : '支払い方法',
      payBtn       : '支払う',
      processing   : '処理中…',
      successTtl   : '🎉 支払いが完了しました',
      successMsg   : (owner)=>`${owner} さんへ送金が完了しました！\n\nPayMeアカウントを作成して、簡単にお金を受け取りましょう。\nhttps://www.payme.co.jp`,
      errorTtl     : 'エラー',
      ok           : 'OK',
      invalidAmt   : '最低金額は50円です',
      missingInfo  : '名前・メール・金額は必須です',
      done         : '完了',
    },
    pt: {
      pageTitle    : 'Enviar dinheiro para',
      amountLabel  : 'Valor (¥ – mínimo 50)',
      nameLabel    : 'Seu Nome *',
      emailLabel   : 'Seu E-mail *',
      methodLabel  : 'Método de pagamento',
      payBtn       : 'Pagar',
      processing   : 'Processando…',
      successTtl   : '🎉 Pagamento efetuado',
      successMsg   : (owner)=>`Você enviou dinheiro para ${owner}!\n\nCrie uma conta PayMe grátis e comece a receber pagamentos.\nhttps://www.payme.co.jp`,
      errorTtl     : 'Erro',
      ok           : 'OK',
      invalidAmt   : 'O mínimo é ¥50',
      missingInfo  : 'Nome, e-mail e valor são obrigatórios',
      done         : 'Concluir',
    },
  }
  const t = i18n[lang] || i18n.en

  /* ─── state ─── */
  const [linkInfo, setLinkInfo] = useState(null)

  const [amount,     setAmount]     = useState('')
  const [payerName,  setPayerName]  = useState('')
  const [payerEmail, setPayerEmail] = useState('')
  const [payMethod,  setPayMethod]  = useState('wallet')

  const [pr, setPr]  = useState(null)
  const prRef = useRef(null)

  const [loading,    setLoading]    = useState(true)
  const [processing, setProcessing] = useState(false)
  const [bankInstr,  setBankInstr]  = useState(null)

  const [showSuccess, setShowSuccess] = useState(false)
  const [showError,   setShowError]   = useState(false)
  const [errorMsg,    setErrorMsg]    = useState('')

  /* ──────────────────────────────────────────────────────────
     1)  Fetch link details
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await axios.get(
          `https://payme-backend-713827320947.asia-northeast1.run.app/api/quickpay/tx/${token}`
        )
        setLinkInfo(data)
        if (data.preset_amount) setAmount(String(data.preset_amount))
      } catch {
        setErrorMsg('Invalid or expired link.')
        setShowError(true)
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [token])

  /* ──────────────────────────────────────────────────────────
     2)  Build PaymentRequest only when available
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!stripe || !linkInfo) return
    setPr(null); prRef.current = null

    const amt = parseInt(amount, 10)
    if (!amt || amt < 50 || payMethod !== 'wallet') return

    const prTemp = stripe.paymentRequest({
      country:'JP',
      currency:'jpy',
      total:{ label:`Pay ${linkInfo.recipient_name}`, amount:amt },
      requestPayerName : true,
      requestPayerEmail: true,
    })

    prTemp.canMakePayment().then((r)=>{
      if (r && (r.applePay || r.googlePay)) {
        prRef.current = prTemp
        setPr(prTemp)
      }
    })
  }, [stripe, linkInfo, amount, payMethod])

  /* PaymentRequest event handler */
  useEffect(() => {
    if (!prRef.current) return
    const prTemp = prRef.current

    const handler = async (ev) => {
      try {
        const name  = ev.payerName  || payerName
        const email = ev.payerEmail || payerEmail
        if (!name || !email || !amount) {
          ev.complete('fail')
          throw new Error(t.missingInfo)
        }

        const { data } = await axios.post(
          `https://payme-backend-713827320947.asia-northeast1.run.app/api/quickpay/tx/${token}/pay`,
          { amount:parseInt(amount,10), paymentMethodType:'wallet', payerName:name, payerEmail:email }
        )

        const { clientSecret } = data
        if (!clientSecret) throw new Error('No client secret')

        const attach = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions:false }
        )
        if (attach.error) { ev.complete('fail'); throw attach.error }

        ev.complete('success')

        if (attach.paymentIntent.status === 'requires_action') {
          const act = await stripe.confirmCardPayment(clientSecret)
          if (act.error) throw act.error
        }
        setShowSuccess(true)
      } catch (err) {
        console.error(err)
        setErrorMsg(err.message || 'Payment failed')
        setShowError(true)
      }
    }

    prTemp.on('paymentmethod', handler)
    return () => prTemp.off('paymentmethod', handler)
  }, [stripe, amount, payerName, payerEmail, t.missingInfo, token])

  /* ──────────────────────────────────────────────────────────
     3)  Traditional form submit
  ────────────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (processing) return
    setProcessing(true)

    const amt = parseInt(amount, 10)
    if (!amt || amt < 50)   { setErrorMsg(t.invalidAmt); setShowError(true); setProcessing(false); return }
    if (!payerName || !payerEmail) { setErrorMsg(t.missingInfo); setShowError(true); setProcessing(false); return }

    try {
      const { data } = await axios.post(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/quickpay/tx/${token}/pay`,
        { amount:amt, paymentMethodType:payMethod, payerName, payerEmail }
      )

      const { clientSecret, bankInstructions } = data
      if (bankInstructions) {
        setBankInstr(bankInstructions); setProcessing(false); return
      }
      if (!clientSecret) throw new Error('No client secret')

      if (payMethod === 'cc' || payMethod === 'jcb') {
        const card = elements.getElement(CardElement)
        const { error } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method:{ card, billing_details:{ name:payerName, email:payerEmail } } }
        )
        if (error) throw error
      }
      setShowSuccess(true)
    } catch (err) {
      console.error(err)
      setErrorMsg(err.message || 'Payment failed')
      setShowError(true)
    }
    setProcessing(false)
  }

  /* ──────────────────────────────────────────────────────────
     4)  UI
  ────────────────────────────────────────────────────────── */
  if (loading) return <p className="m-4">{t.processing}</p>

  const owner = linkInfo?.recipient_name || 'User'

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-6">
        {/* logo & heading */}
        <div className="text-center">
          <img
            src="/payme-logo.png"
            alt="PayMe"
            className="mx-auto h-28 w-auto mb-3"
          />
          <h2 className="text-2xl font-bold text-gray-700">
            {t.pageTitle} {owner}
          </h2>
        </div>

        {/* card */}
        <div className="rounded-lg shadow-lg border border-gray-200 p-6 bg-white space-y-5">
          <form onSubmit={handleSubmit} className="space-y-5">
            {!linkInfo?.preset_amount && (
              <div>
                <label className="block mb-1 text-gray-700 font-medium">
                  {t.amountLabel}
                </label>
                <input
                  type="number"
                  min="50"
                  value={amount}
                  onChange={e=>setAmount(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
                />
              </div>
            )}

            <div>
              <label className="block mb-1 text-gray-700 font-medium">
                {t.nameLabel}
              </label>
              <input
                type="text"
                value={payerName}
                onChange={e=>setPayerName(e.target.value)}
                required
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block mb-1 text-gray-700 font-medium">
                {t.emailLabel}
              </label>
              <input
                type="email"
                value={payerEmail}
                onChange={e=>setPayerEmail(e.target.value)}
                required
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block mb-1 text-gray-700 font-medium">
                {t.methodLabel}
              </label>
              <select
                value={payMethod}
                onChange={e=>setPayMethod(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                <option value="wallet">Apple / Google Pay</option>
                <option value="cc">Credit Card (Visa/Mastercard)</option>
                <option value="jcb">Credit Card (JCB)</option>
                <option value="bank_transfer">Bank Transfer (Furikomi)</option>
              </select>
            </div>

            {(payMethod === 'cc' || payMethod === 'jcb') && (
              <>
                <label className="block mb-1 text-gray-700 font-medium">
                  Card Details
                </label>
                <div className="border rounded px-2 py-3">
                  <CardElement options={{ hidePostalCode:true }} />
                </div>
              </>
            )}

            {payMethod !== 'wallet' && (
              <button
                type="submit"
                disabled={processing}
                className={`w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded transition-colors ${
                  processing
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:bg-blue-700'
                }`}
              >
                {processing ? t.processing : t.payBtn}
              </button>
            )}
          </form>

          {bankInstr && (
            <div className="p-4 bg-yellow-50 border rounded text-sm whitespace-pre-wrap break-words">
              {JSON.stringify(bankInstr, null, 2)}
            </div>
          )}

          {payMethod === 'wallet' && pr && (
            <PaymentRequestButtonElement
              options={{
                paymentRequest: pr,
                style:{ paymentRequestButton:{ height:'44px' } },
                enableLink:false,
              }}
            />
          )}
        </div>

        <div className="text-center">
          <button onClick={()=>navigate(-1)} className="text-blue-600 underline">
            {t.done}
          </button>
        </div>

        {/* spinner overlay */}
        {processing && (
          <div className="fixed inset-0 flex items-center justify-center bg-white/70 z-50">
            <svg
              className="animate-spin h-10 w-10 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-96 max-w-full p-6 mx-4">
            <h4 className="text-xl font-semibold mb-4 text-green-600">
              {t.successTtl}
            </h4>
            <p className="whitespace-pre-wrap">{t.successMsg(owner)}</p>
            <div className="mt-6 flex justify-end">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={()=>{ setShowSuccess(false); navigate('/') }}
              >
                {t.ok}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-96 max-w-full p-6 mx-4">
            <h4 className="text-xl font-semibold mb-4 text-red-600">
              {t.errorTtl}
            </h4>
            <p className="whitespace-pre-wrap">{errorMsg}</p>
            <div className="mt-6 flex justify-end">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={()=>setShowError(false)}
              >
                {t.ok}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuickPayGuestPage
// ────────────────────────────────────────────────────────────────
// END – QuickPayGuestPage.js
// ────────────────────────────────────────────────────────────────
